import { useEffect, useState, useRef, useMemo, useCallback, Fragment } from 'react';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { updateUserDetail } from '../../redux/reducer';
import { useDispatch } from 'react-redux';
import { Ic } from '../../shared/component/GosIcon';

/* Role Management — GrowthOS accordion design (matched to growthos/RoleManagement.jsx).
   Pick a role, switch between Permissions / UI Visibility, then expand each module and
   toggle what that role can do and see. All wiring is the REAL role-permission logic
   ported from the legacy left-nav page (getRoleList / getPermissionGroup /
   getPermissionsByRole / savePermissionsByRole for Action+Field permissions;
   getUiPermissions / getUiPermissionsByRoleId / saveUiPermissionsByRole for UI
   Visibility, with the exact menu→screen→field cascade + save-format the legacy used).

   Design-vs-data mapping notes:
   - Permissions tab keeps the design's Action / Field sub-segment. Real action-permissions
     of both types are single toggles saved as a flat permissionIds list, so both sub-tabs
     render as checkbox grids (the design's Field View/Edit table is reused for UI Visibility
     instead, where real canView/canEdit exist).
   - UI Visibility maps onto the real nested Menu → Screen → Field tree: a module's menu is a
     checkbox; when on, its screens/fields show in a View/Edit table (screen = View only,
     field = View + Edit), applying the legacy cascade rules. The design's mock flat element
     list (Menu Item / List View / …) is replaced by the real screen/field names. */

function useOutside(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, onClose]);
  return ref;
}

function Switch({ on, onClick, title }) {
  return (
    <button
      type="button"
      className={'gos-rm-sw' + (on ? ' on' : '')}
      onClick={onClick}
      title={title}
      aria-pressed={!!on}
    />
  );
}

function RoleManagement({ embedded = false }) {
  const dispatch = useDispatch();

  const [roles, setRoles] = useState([]); // [{id, name}]
  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDdOpen, setRoleDdOpen] = useState(false);
  const roleDdRef = useOutside(roleDdOpen, () => setRoleDdOpen(false));

  const [tab, setTab] = useState('perms'); // 'perms' | 'ui'
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [subByMod, setSubByMod] = useState({});
  const [loading, setLoading] = useState(false);

  // Action / Field permissions
  const [permCatalog, setPermCatalog] = useState({}); // { module: { action:{name:{id,displayName}}, field:{...}, ui:{...} } }
  const [permIds, setPermIds] = useState([]); // checked permission ids
  const [permsDirty, setPermsDirty] = useState(false);

  // UI Visibility
  const [uiCatalog, setUiCatalog] = useState(null); // { module: { menuPermissions:[ menu{ screenPermissions:[ screen{ fieldPermissions:[] } ] } ] } }
  const [uiData, setUiData] = useState(null); // role-applied clone of the catalog
  const [uiDirty, setUiDirty] = useState(false);

  const [selfRefresh, setSelfRefresh] = useState(false);
  const accountDetail = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetail'));
    } catch {
      return null;
    }
  }, []);

  /* ── roles list ── */
  useEffect(() => {
    apiServices
      .getRoleList()
      .then((res) => {
        const list = (res.data.rolesList || [])
          .map((r) => ({ id: r.id, name: r.roleName }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setRoles(list);
      })
      .catch((e) => console.error('getRoleList', e));
  }, []);

  /* ── permission catalogue (Action / Field) ── */
  useEffect(() => {
    apiServices
      .getPermissionGroup()
      .then((res) => {
        const tmp = res.data.permissionList || [];
        const map = {};
        tmp.forEach((g) => {
          const { moduleName, permissionDisplayName, permissionId, permissionType } = g;
          if (!moduleName) return;
          if (!map[moduleName]) map[moduleName] = { action: {}, field: {}, ui: {} };
          const t = (permissionType || '').toLowerCase();
          const vt = ['action', 'field', 'ui'].includes(t) ? t : 'action';
          map[moduleName][vt][permissionDisplayName] = {
            id: permissionId,
            displayName: permissionDisplayName,
          };
        });
        setPermCatalog(map);
      })
      .catch((e) => console.error('getPermissionGroup', e));
  }, []);

  /* ── UI permission catalogue (menu → screen → field tree) ── */
  useEffect(() => {
    apiServices
      .getUiPermissions()
      .then((res) => {
        const tmp = res.data.uiPermissionList || [];
        const fmt = {};
        tmp.forEach((p) => {
          const m = p.moduleName;
          if (!m) return;
          if (!fmt[m]) fmt[m] = { menuPermissions: [], screens: [], fields: [] };
          if (p.permissionTypeName === 'Menu') fmt[m].menuPermissions.push({ ...p });
          else if (p.permissionTypeName === 'Screen') fmt[m].screens.push({ ...p });
          else if (p.permissionTypeName === 'Field') fmt[m].fields.push({ ...p });
        });
        Object.values(fmt).forEach((md) => {
          md.menuPermissions.forEach((menu) => {
            menu.screenPermissions = [];
            md.screens.forEach((screen) => {
              if (screen.permissionParentId === menu.permissionId) {
                screen.fieldPermissions = [];
                md.fields.forEach((field) => {
                  if (field.permissionParentId === screen.permissionId)
                    screen.fieldPermissions.push(field);
                });
                menu.screenPermissions.push(screen);
              }
            });
          });
        });
        Object.keys(fmt).forEach((m) => {
          delete fmt[m].screens;
          delete fmt[m].fields;
        });
        setUiCatalog(fmt);
      })
      .catch((e) => console.error('getUiPermissions', e));
  }, []);

  /* ── load Action/Field permissions for the selected role ── */
  const loadRolePerms = useCallback(() => {
    if (!roleId) return;
    setLoading(true);
    apiServices
      .getPermissionsByRole(roleId)
      .then((res) => {
        const rp = res.data?.role?.rolePermissions || [];
        setPermIds([...new Set(rp.map((p) => p.permissionId))]);
        setPermsDirty(false);
      })
      .catch((e) => console.error('getPermissionsByRole', e))
      .finally(() => setLoading(false));
  }, [roleId]);

  useEffect(() => {
    loadRolePerms();
  }, [loadRolePerms]);

  /* ── load UI Visibility for the selected role (apply onto a clean clone) ── */
  const loadRoleUi = useCallback(() => {
    if (!roleId || !uiCatalog) return;
    setLoading(true);
    apiServices
      .getUiPermissionsByRoleId(roleId)
      .then((res) => {
        const rows = res.data?.roleUiPermissions || [];
        const byId = {};
        rows.forEach((p) => {
          byId[p.permissionId] = p;
        });
        const tree = JSON.parse(JSON.stringify(uiCatalog));
        const apply = (node) => {
          const c = byId[node.permissionId];
          node.isChecked = !!c;
          node.canView = c ? !!c.canView : false;
          node.canEdit = c ? !!c.canEdit : false;
          node.canCreate = c ? !!c.canCreate : false;
          node.canDelete = c ? !!c.canDelete : false;
        };
        Object.keys(tree).forEach((mk) => {
          tree[mk].menuPermissions.forEach((menu) => {
            apply(menu);
            (menu.screenPermissions || []).forEach((sc) => {
              apply(sc);
              (sc.fieldPermissions || []).forEach((f) => apply(f));
            });
          });
        });
        setUiData(tree);
        setUiDirty(false);
      })
      .catch((e) => console.error('getUiPermissionsByRoleId', e))
      .finally(() => setLoading(false));
  }, [roleId, uiCatalog]);

  useEffect(() => {
    loadRoleUi();
  }, [loadRoleUi]);

  /* ── build the saveUiPermissionsByRole payload from the whole tree ── */
  const buildUiSave = useCallback(
    (tree) => {
      const roleAndUiPermissions = [];
      Object.keys(tree || {}).forEach((m) => {
        tree[m].menuPermissions.forEach((menu) => {
          roleAndUiPermissions.push({
            roleId,
            uiPermissionId: menu.permissionId,
            canCreate: !!menu.canCreate,
            canDelete: !!menu.canDelete,
            canEdit: !!menu.canEdit,
            canView: !!menu.canView,
          });
          (menu.screenPermissions || []).forEach((sc) => {
            roleAndUiPermissions.push({
              roleId,
              uiPermissionId: sc.permissionId,
              canCreate: true,
              canDelete: true,
              canEdit: true,
              canView: !!sc.canView,
            });
            (sc.fieldPermissions || []).forEach((f) => {
              roleAndUiPermissions.push({
                roleId,
                uiPermissionId: f.permissionId,
                canCreate: true,
                canDelete: true,
                canEdit: !!f.canEdit,
                canView: !!f.canView,
              });
            });
          });
        });
      });
      return { roleUiPermissionsVm: { roleAndUiPermissions } };
    },
    [roleId]
  );

  /* ── refresh the logged-in user's own permissions after an action-perm save ── */
  useEffect(() => {
    if (!accountDetail || !selfRefresh) return;
    apiServices
      .getUserDetail(accountDetail.localAccountId)
      .then((res) => {
        if (res.status !== 200) return;
        setSelfRefresh(false);
        localStorage.setItem('userInformation', JSON.stringify(res.data.user));
        dispatch(updateUserDetail({ userInformation: res.data.user }));
        const data = res.data.user.userPermissions || [];
        const uniq = [...new Set(data.map((p) => p.permissionValue))];
        const denied = [
          ...new Set((res.data.user.permissionsDenied || []).map((p) => p.permissionValue)),
        ];
        const filtered = uniq.filter((p) => !denied.includes(p));
        const granted = [
          ...new Set((res.data.user.permissionsGranted || []).map((p) => p.permissionValue)),
        ];
        granted.forEach((p) => {
          if (!filtered.includes(p)) filtered.push(p);
        });
        dispatch(updateUserDetail({ accessPermissions: filtered }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfRefresh]);

  /* ── role select ── */
  const selectRole = (r) => {
    setRoleId(r.id);
    setRoleName(r.name);
    setPermsDirty(false);
    setUiDirty(false);
    setExpanded({});
  };

  /* ── Permissions helpers ── */
  const permEntries = useCallback(
    (m) => {
      const mod = permCatalog[m] || {};
      return [...Object.values(mod.action || {}), ...Object.values(mod.field || {})];
    },
    [permCatalog]
  );
  const permIdSet = useMemo(() => new Set(permIds), [permIds]);
  const permCount = (m) => permEntries(m).filter((e) => permIdSet.has(e.id)).length;
  const permTotal = (m) => permEntries(m).length;
  const togglePerm = (id) => {
    setPermIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setPermsDirty(true);
  };
  const setAllPerms = (m, val) => {
    const ids = permEntries(m).map((e) => e.id);
    setPermIds((prev) => {
      const set = new Set(prev);
      ids.forEach((id) => (val ? set.add(id) : set.delete(id)));
      return [...set];
    });
    setPermsDirty(true);
  };

  /* ── UI Visibility helpers ── */
  const uiNodes = useCallback(
    (m) => {
      const out = [];
      (uiData?.[m]?.menuPermissions || []).forEach((menu) => {
        out.push(menu);
        (menu.screenPermissions || []).forEach((sc) => {
          out.push(sc);
          (sc.fieldPermissions || []).forEach((f) => out.push(f));
        });
      });
      return out;
    },
    [uiData]
  );
  const uiCount = (m) => uiNodes(m).filter((n) => n.canView).length;
  const uiTotal = (m) => uiNodes(m).length;

  const toggleUi = (kind, moduleName, permissionId) => {
    setUiData((prev) => {
      const data = JSON.parse(JSON.stringify(prev));
      const menus = data[moduleName].menuPermissions;
      if (kind === 'menu') {
        const menu = menus.find((x) => x.permissionId === permissionId);
        if (menu) {
          menu.isChecked = !menu.isChecked;
          menu.canView = menu.isChecked;
          if (!menu.isChecked) {
            (menu.screenPermissions || []).forEach((sc) => {
              sc.isChecked = false;
              sc.canView = false;
              (sc.fieldPermissions || []).forEach((f) => {
                f.isChecked = false;
                f.canEdit = false;
                f.canView = false;
              });
            });
          }
        }
      } else if (kind === 'screen') {
        let pm = null;
        let screen = null;
        for (const menu of menus) {
          const s = (menu.screenPermissions || []).find((x) => x.permissionId === permissionId);
          if (s) {
            pm = menu;
            screen = s;
            break;
          }
        }
        if (screen) {
          screen.isChecked = !screen.isChecked;
          screen.canView = screen.isChecked;
          if (screen.isChecked && pm) {
            pm.isChecked = true;
            pm.canView = true;
          }
          if (!screen.isChecked)
            (screen.fieldPermissions || []).forEach((f) => {
              f.isChecked = false;
              f.canEdit = false;
              f.canView = false;
            });
        }
      } else if (kind === 'fieldView' || kind === 'fieldEdit') {
        let pm = null;
        let ps = null;
        let field = null;
        for (const menu of menus) {
          for (const sc of menu.screenPermissions || []) {
            const f = (sc.fieldPermissions || []).find((x) => x.permissionId === permissionId);
            if (f) {
              pm = menu;
              ps = sc;
              field = f;
              break;
            }
          }
          if (field) break;
        }
        if (field) {
          if (kind === 'fieldEdit') field.canEdit = !field.canEdit;
          else field.canView = !field.canView;
          if (field.canEdit && !field.canView) field.canView = true;
          if (field.canEdit || field.canView) {
            field.isChecked = true;
            if (ps) {
              ps.isChecked = true;
              ps.canView = true;
            }
            if (pm) {
              pm.isChecked = true;
              pm.canView = true;
            }
          } else {
            field.isChecked = false;
          }
        }
      }
      return data;
    });
    setUiDirty(true);
  };

  const setAllUi = (m, val) => {
    setUiData((prev) => {
      const data = JSON.parse(JSON.stringify(prev));
      (data[m].menuPermissions || []).forEach((menu) => {
        menu.isChecked = val;
        menu.canView = val;
        (menu.screenPermissions || []).forEach((sc) => {
          sc.isChecked = val;
          sc.canView = val;
          (sc.fieldPermissions || []).forEach((f) => {
            f.isChecked = val;
            f.canView = val;
            if (!val) f.canEdit = false;
          });
        });
      });
      return data;
    });
    setUiDirty(true);
  };

  /* ── modules shown for the active tab, filtered by search ── */
  const modules = useMemo(() => {
    const all = tab === 'perms' ? Object.keys(permCatalog) : Object.keys(uiData || uiCatalog || {});
    all.sort((a, b) => a.localeCompare(b));
    const term = q.trim().toLowerCase();
    return term ? all.filter((m) => (m + ' Module').toLowerCase().includes(term)) : all;
  }, [tab, permCatalog, uiData, uiCatalog, q]);

  const countOn = (m) => (tab === 'perms' ? permCount(m) : uiCount(m));
  const totalOf = (m) => (tab === 'perms' ? permTotal(m) : uiTotal(m));
  const setAll = (m, val) => (tab === 'perms' ? setAllPerms(m, val) : setAllUi(m, val));

  const dirty = tab === 'perms' ? permsDirty : uiDirty;

  const save = () => {
    if (!roleId) return;
    if (tab === 'perms') {
      setLoading(true);
      apiServices
        .savePermissionsByRole({ roleAndPermissionMapping: { roleId, permissionIds: permIds } })
        .then((res) => {
          if (res.status === 200) {
            setPermsDirty(false);
            setSelfRefresh(true);
            toast.success('Action Permission updated successfully!', { position: 'top-right' });
          }
        })
        .catch((e) => toast.error(`${e.message}`, { position: 'top-right' }))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      apiServices
        .saveUiPermissionsByRole(buildUiSave(uiData))
        .then((res) => {
          if (res.status === 200) {
            setUiDirty(false);
            toast.success('UI Permissions updated successfully!', { position: 'top-right' });
          }
        })
        .catch((e) => toast.error(`${e.message}`, { position: 'top-right' }))
        .finally(() => setLoading(false));
    }
  };

  const discard = () => {
    if (tab === 'perms') loadRolePerms();
    else loadRoleUi();
  };

  /* ── per-module body ── */
  const renderPermBody = (m) => {
    const sub = subByMod[m] || 'action';
    const mod = permCatalog[m] || {};
    const entries = Object.values(mod[sub] || {});
    return (
      <>
        <div className="gos-rm-subseg">
          <button
            type="button"
            className={'gos-rm-subbtn' + (sub === 'action' ? ' on' : '')}
            onClick={() => setSubByMod((s) => ({ ...s, [m]: 'action' }))}
          >
            Action
          </button>
          <button
            type="button"
            className={'gos-rm-subbtn' + (sub === 'field' ? ' on' : '')}
            onClick={() => setSubByMod((s) => ({ ...s, [m]: 'field' }))}
          >
            Field
          </button>
        </div>
        {entries.length === 0 ? (
          <div className="gos-rm-empty">No {sub} permissions for this module.</div>
        ) : (
          <div className="gos-rm-grid">
            {entries.map((e) => {
              const on = permIdSet.has(e.id);
              return (
                <button
                  type="button"
                  key={e.id}
                  className={'gos-rm-chk' + (on ? ' on' : '')}
                  onClick={() => togglePerm(e.id)}
                >
                  <span className="gos-rm-chk-box">{on ? <Ic name="check" size={12} /> : null}</span>
                  <span className="gos-rm-chk-label" title={e.displayName}>
                    {e.displayName}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderUiBody = (m) => {
    const menus = uiData?.[m]?.menuPermissions || [];
    if (!menus.length) return <div className="gos-rm-empty">No UI elements for this module.</div>;
    return (
      <div className="gos-rm-uigroups">
        {menus.map((menu) => (
          <div className="gos-rm-uigroup" key={menu.permissionId}>
            <button
              type="button"
              className={'gos-rm-chk gos-rm-chk-wide' + (menu.canView ? ' on' : '')}
              onClick={() => toggleUi('menu', m, menu.permissionId)}
            >
              <span className="gos-rm-chk-box">
                {menu.canView ? <Ic name="check" size={12} /> : null}
              </span>
              <span className="gos-rm-chk-label" title={menu.permissionDisplayName}>
                {menu.permissionDisplayName}
              </span>
            </button>
            {menu.canView && (menu.screenPermissions || []).length > 0 && (
              <div className="gos-rm-ftbl">
                <div className="gos-rm-fhead">
                  <span>Screen / Field</span>
                  <span>View</span>
                  <span>Edit</span>
                </div>
                {(menu.screenPermissions || []).map((sc) => (
                  <Fragment key={sc.permissionId}>
                    <div className="gos-rm-frow">
                      <span className="gos-rm-fname gos-rm-fname-screen">
                        {sc.permissionDisplayName}
                      </span>
                      <button
                        type="button"
                        className={'gos-rm-fcell' + (sc.canView ? ' on' : '')}
                        onClick={() => toggleUi('screen', m, sc.permissionId)}
                      >
                        {sc.canView ? <Ic name="check" size={12} /> : null}
                      </button>
                      <span className="gos-rm-fcell-empty">—</span>
                    </div>
                    {(sc.fieldPermissions || []).map((f) => (
                      <div className="gos-rm-frow" key={f.permissionId}>
                        <span className="gos-rm-fname gos-rm-fname-field">
                          {f.permissionDisplayName}
                        </span>
                        <button
                          type="button"
                          className={'gos-rm-fcell' + (f.canView ? ' on' : '')}
                          onClick={() => toggleUi('fieldView', m, f.permissionId)}
                        >
                          {f.canView ? <Ic name="check" size={12} /> : null}
                        </button>
                        <button
                          type="button"
                          className={'gos-rm-fcell' + (f.canEdit ? ' on' : '')}
                          onClick={() => toggleUi('fieldEdit', m, f.permissionId)}
                        >
                          {f.canEdit ? <Ic name="check" size={12} /> : null}
                        </button>
                      </div>
                    ))}
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const body = (
    <div className="gos-rm">
      <div className="gos-rm-head">
        <div>
          <div className="gos-rm-title">Role Management</div>
          <div className="gos-rm-sub">
            Choose a role, then set what it can do and see — module by module.
          </div>
        </div>
        <div className="gos-rm-roledd" ref={roleDdRef}>
          <button
            type="button"
            className={'gos-rm-roledd-btn' + (roleDdOpen ? ' open' : '')}
            onClick={() => setRoleDdOpen((o) => !o)}
          >
            <span className="gos-rm-roledd-ic">
              <Ic name="shield" size={15} />
            </span>
            <span className="gos-rm-roledd-txt">
              <span className="gos-rm-roledd-cap">Role</span>
              <span className="gos-rm-roledd-val">{roleName || 'Select a role'}</span>
            </span>
            <Ic name="chevD" size={16} />
          </button>
          {roleDdOpen && (
            <div className="gos-rm-roledd-menu">
              {roles.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  className="gos-rm-roledd-item"
                  onClick={() => {
                    selectRole(r);
                    setRoleDdOpen(false);
                  }}
                >
                  <span className={'gos-rm-check' + (r.id === roleId ? ' on' : '')}>
                    {r.id === roleId ? <Ic name="check" size={11} /> : null}
                  </span>
                  {r.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="gos-rm-bar">
        <div className="gos-rm-seg">
          <button
            type="button"
            className={'gos-rm-seg-btn' + (tab === 'perms' ? ' on' : '')}
            onClick={() => setTab('perms')}
          >
            <Ic name="lock" size={15} />
            Permissions
          </button>
          <button
            type="button"
            className={'gos-rm-seg-btn' + (tab === 'ui' ? ' on' : '')}
            onClick={() => setTab('ui')}
          >
            <Ic name="eye" size={15} />
            UI Visibility
          </button>
        </div>
        <div className="gos-rm-grow" />
        <div className="gos-rm-search">
          <Ic name="search" size={15} />
          <input
            placeholder="Search modules…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q ? (
            <button type="button" className="gos-rm-clear" onClick={() => setQ('')}>
              <Ic name="x" size={14} />
            </button>
          ) : null}
        </div>
      </div>

      {!roleId ? (
        <div className="gos-rm-noselect">
          <span className="gos-rm-noselect-ic">
            <Ic name="shield" size={30} />
          </span>
          <h3>Select a role to begin</h3>
          <p>Choose a role from the dropdown above to view and edit its permissions.</p>
        </div>
      ) : loading && modules.length === 0 ? (
        <div className="gos-rm-list">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="gos-skel-box gos-skel-an gos-rm-skel" />
          ))}
        </div>
      ) : (
        <div className="gos-rm-list">
          {modules.length === 0 ? (
            <div className="gos-rm-empty gos-rm-empty-lg">No modules match your search.</div>
          ) : (
            modules.map((m) => {
              const open = !!expanded[m];
              const on = countOn(m);
              const tot = totalOf(m);
              const allOn = tot > 0 && on === tot;
              return (
                <div key={m} className={'gos-rm-mod' + (open ? ' open' : '')}>
                  <div className="gos-rm-modhead">
                    <button
                      type="button"
                      className="gos-rm-modmain"
                      onClick={() => setExpanded((e) => ({ ...e, [m]: !open }))}
                    >
                      <span className="gos-rm-caret">
                        <Ic name="chevR" size={16} />
                      </span>
                      <span className="gos-rm-modtitle">{m} Module</span>
                      <span className={'gos-rm-count' + (on ? '' : ' zero')}>
                        {on} / {tot}
                      </span>
                    </button>
                    <label className="gos-rm-master" title={allOn ? 'Disable all' : 'Enable all'}>
                      <span className="gos-rm-master-txt">
                        {allOn ? 'All' : on ? 'Partial' : 'None'}
                      </span>
                      <Switch on={allOn} onClick={() => setAll(m, !allOn)} />
                    </label>
                  </div>
                  {open && (
                    <div className="gos-rm-modbody">
                      {tab === 'perms' ? renderPermBody(m) : renderUiBody(m)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {roleId && dirty && (
        <div className="gos-rm-savebar">
          <span className="gos-rm-savebar-note">
            <span className="gos-rm-dot" />
            Unsaved changes to <strong>{roleName}</strong>
          </span>
          <div className="gos-rm-savebar-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={discard}>
              Discard
            </button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={save}>
              <Ic name="check" size={15} />
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return embedded ? body : <div className="inner-page">{body}</div>;
}

export default RoleManagement;
